import { helperApi } from '../api/endpoints';
import { stationDbName } from './localStation';
import { StationStore } from './store';
import {
  flushOutbox,
  type FlushOptions,
  type FlushSummary,
  type PersonSource,
  type ReturnScanApi,
} from './sync';

/**
 * Sendet die Warteschlange einer Station unter einer Sperre je Basar: Die Stationsseite und der Lauf vor dem Abmelden senden nie
 * gleichzeitig (sonst kämen Scans doppelt oder außer der Reihe an). Hält ein anderer die Sperre, ist es `'busy'` (oder, mit `wait`, wird bis zum Abbruch gewartet); ohne Web Locks
 * verhindert ein Flag im Modul das doppelte Senden.
 */
export async function flushStationLocked(
  store: StationStore,
  api: ReturnScanApi,
  eventId: string,
  person: PersonSource,
  options: FlushOptions & {
    /** Auf die Sperre warten (bis zum Abbruch), statt bei einem laufenden Lauf sofort `'busy'` zu melden. */
    wait?: boolean;
  } = {},
): Promise<FlushSummary | 'busy'> {
  const locks: LockManager | undefined = 'locks' in navigator ? navigator.locks : undefined;
  if (locks === undefined) {
    // Ohne Web Locks: ein Flag im Modul verhindert wenigstens doppeltes Senden aus diesem Fenster
    if (runningWithoutLocks.has(eventId)) return 'busy';
    runningWithoutLocks.add(eventId);
    try {
      return await flushOutbox(store, api, eventId, person, options);
    } finally {
      runningWithoutLocks.delete(eventId);
    }
  }
  const name = `barso-station-flush-${eventId}`;
  const run = async (lock: Lock | null): Promise<FlushSummary | 'busy'> =>
    lock === null ? 'busy' : flushOutbox(store, api, eventId, person, options);
  if (options.wait === true) {
    return locks.request(name, options.signal === undefined ? {} : { signal: options.signal }, run);
  }
  return locks.request(name, { ifAvailable: true }, run);
}

const runningWithoutLocks = new Set<string>();

const STATION_PREFIX = 'station-';

/**
 * Vor dem Abmelden: Alle wartenden Rückgabe-Scans dieser Person aller Stationen dieses Geräts senden, solange ihr Konto noch
 * angemeldet ist. Was nicht hinausgeht (kein Netz, Fehler, Sperre), bleibt in der Warteschlange und geht unter ihrem Konto
 * hinaus, wenn sie sich wieder anmeldet; unter dem Konto der nächsten Person nie. Wirft nie.
 */
export async function flushAllStations(
  person: PersonSource,
  api: ReturnScanApi = helperApi,
  options: FlushOptions = {},
): Promise<void> {
  if (!navigator.onLine) return;
  try {
    if (typeof indexedDB.databases !== 'function') return;
    const names = (await indexedDB.databases()).flatMap((entry) =>
      entry.name?.startsWith(STATION_PREFIX) === true ? [entry.name] : [],
    );
    for (const name of names) {
      if (options.signal?.aborted === true) return;
      const eventId = name.slice(STATION_PREFIX.length);
      if (stationDbName(eventId) !== name) continue;
      try {
        const store = await StationStore.open(name);
        try {
          await flushStationLocked(store, api, eventId, person, { ...options, wait: true });
        } finally {
          store.close();
        }
      } catch {
        // Speicher gesperrt oder Fehler: die Scans bleiben erhalten
      }
    }
  } catch {
    // ohne Auflistung der Datenbanken gibt es nichts zu senden
  }
}
